import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import ReactPlayer from "react-player";
import { FaPause, FaPlay } from "react-icons/fa";
import * as S from "../../css/VassTestPlayer.styles";

const PLAYBACK_RATES = [0.5, 1, 1.25, 1.5, 2];
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2;
const ZOOM_STEP = 0.1;

function VassTestPlayer(props) {
  // NOTE 테스트용 플레이어 제어 상태
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [played, setPlayed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted] = useState(true);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [zoomScale, setZoomScale] = useState(1);

  // NOTE 초 단위를 hh:mm:ss 또는 mm:ss로 변환
  const formatTime = (seconds) => {
    const safeSeconds = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const secs = Math.floor(safeSeconds % 60);
    const paddedMinutes = String(minutes).padStart(2, "0");
    const paddedSeconds = String(secs).padStart(2, "0");

    if (hours > 0) {
      return `${hours}:${paddedMinutes}:${paddedSeconds}`;
    }
    return `${minutes}:${paddedSeconds}`;
  };

  // NOTE 확대/축소는 스트림 재요청 없이 CSS transform으로 처리
  const handleZoomIn = () => {
    setZoomScale((prev) =>
      Math.min(MAX_ZOOM, Number((prev + ZOOM_STEP).toFixed(2)))
    );
  };

  const handleZoomOut = () => {
    setZoomScale((prev) =>
      Math.max(MIN_ZOOM, Number((prev - ZOOM_STEP).toFixed(2)))
    );
  };

  // NOTE 진행바 이동 시 현재 재생 위치를 갱신
  const handleSeekChange = (event) => {
    setPlayed(Number(event.target.value));
  };

  const handleSeekCommit = (event) => {
    const nextPlayed = Number(event.target.value);
    setPlayed(nextPlayed);
    if (playerRef.current) {
      playerRef.current.seekTo(nextPlayed);
    }
  };

  // NOTE 전체화면 전환 (브라우저 Fullscreen API 사용)
  const handleToggleFullscreen = () => {
    if (!containerRef.current) {
      return;
    }
    if (document.fullscreenElement) {
      document.exitFullscreen();
      return;
    }
    containerRef.current.requestFullscreen();
  };

  return (
    <S.PlayerContainer ref={containerRef}>
      <S.PlayerViewport>
        {/* NOTE 영상 영역만 확대/축소하고 컨트롤바는 고정 */}
        <S.PlayerSurface $scale={zoomScale}>
          <ReactPlayer
            ref={playerRef}
            url={props.url}
            controls={false}
            playing={isPlaying}
            muted={isMuted}
            playbackRate={playbackRate}
            width="100%"
            height="100%"
            onProgress={(state) => setPlayed(state.played)}
            onDuration={(nextDuration) => setDuration(nextDuration)}
            onEnded={() => setIsPlaying(false)}
          />
        </S.PlayerSurface>
        <S.ControlBar>
          <S.ControlGroup>
            <S.IconButton
              onClick={() => setIsPlaying((prev) => !prev)}
              aria-label={isPlaying ? "일시정지" : "재생"}
            >
              {/* NOTE 사용자에게 익숙한 재생/일시정지 아이콘 표시 */}
              {isPlaying ? <FaPause /> : <FaPlay />}
            </S.IconButton>
          </S.ControlGroup>
          <S.ProgressGroup>
            <S.ControlLabel>진행</S.ControlLabel>
            <S.ProgressInput
              type="range"
              min="0"
              max="1"
              step="0.001"
              value={played}
              onChange={handleSeekChange}
              onMouseUp={handleSeekCommit}
              onTouchEnd={handleSeekCommit}
            />
            <S.TimeLabel>
              {formatTime(duration * played)} / {formatTime(duration)}
            </S.TimeLabel>
          </S.ProgressGroup>
          <S.ControlGroup>
            <S.ControlLabel>속도</S.ControlLabel>
            <S.ControlSelect
              value={playbackRate}
              onChange={(event) => setPlaybackRate(Number(event.target.value))}
            >
              {PLAYBACK_RATES.map((rate) => (
                <option key={rate} value={rate}>
                  {rate}x
                </option>
              ))}
            </S.ControlSelect>
          </S.ControlGroup>
          <S.ControlGroup>
            <S.ControlLabel>확대</S.ControlLabel>
            <S.ZoomButton onClick={handleZoomOut} disabled={zoomScale <= MIN_ZOOM}>
              -
            </S.ZoomButton>
            <S.ZoomValue>{Math.round(zoomScale * 100)}%</S.ZoomValue>
            <S.ZoomButton onClick={handleZoomIn} disabled={zoomScale >= MAX_ZOOM}>
              +
            </S.ZoomButton>
          </S.ControlGroup>
          <S.ControlGroup>
            <S.ControlButton onClick={handleToggleFullscreen}>
              전체화면
            </S.ControlButton>
          </S.ControlGroup>
        </S.ControlBar>
      </S.PlayerViewport>
    </S.PlayerContainer>
  );
}

VassTestPlayer.propTypes = {
  // NOTE m3u8 테스트 스트림 주소
  url: PropTypes.string.isRequired,
};

export default VassTestPlayer;
